import { supabase } from './supabase';

/**
 * Cancela un pedido usando la Edge Function para un manejo más robusto
 * @param orderId ID del pedido a cancelar
 * @param userId ID del usuario (opcional para verificación)
 * @returns Promise con el resultado de la operación
 */
export async function cancelOrder(orderId: string, userId?: string) {
  try {
    const { data, error } = await supabase.functions.invoke('cancel-order', {
      body: {
        orderId,
        userId
      }
    });

    if (error) {
      console.error('Error al cancelar pedido:', error);
      throw new Error(error.message || 'Error al cancelar el pedido');
    }

    return {
      success: true,
      message: data?.message || 'Pedido cancelado exitosamente'
    };
  } catch (error) {
    console.error('Error en cancelOrder:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Error inesperado al cancelar el pedido'
    };
  }
}

/**
 * Verifica si un pedido se puede cancelar
 * @param status Estado actual del pedido
 * @returns boolean indicando si se puede cancelar
 */
export function canCancelOrder(status: string): boolean {
  return ['pending', 'pending_payment', 'confirmed'].includes(status);
}

/**
 * Verifica si un pedido permite reintento de pago
 * @param status Estado del pedido
 * @param paymentStatus Estado del pago
 * @param paymentMethod Método de pago (opcional)
 * @returns boolean indicando si se puede reintentar el pago
 */
export function canRetryPayment(status: string, paymentStatus: string, paymentMethod?: string): boolean {
  // Si es transferencia bancaria y está en pending_payment, NO permitir reintento
  // (significa que ya subió el comprobante y está esperando verificación)
  if (paymentMethod === 'bankTransfer' && status === 'pending_payment') {
    return false;
  }
  
  return (
    (status === 'pending' && paymentStatus === 'pending') ||
    (status === 'pending' && paymentStatus === 'failed') ||
    (status === 'pending_payment')
  );
}

/**
 * Obtiene el mensaje apropiado según el estado del pago
 * @param status Estado del pedido
 * @param paymentStatus Estado del pago
 * @param paymentMethod Método de pago (opcional)
 * @returns string con el mensaje informativo
 */
export function getPaymentStatusMessage(status: string, paymentStatus: string, paymentMethod?: string): string {
  if (paymentMethod === 'bankTransfer' && status === 'pending_payment') {
    return 'Hemos recibido tu comprobante de transferencia. Nuestro equipo lo está verificando y te notificaremos cuando sea confirmado.';
  } else if (paymentMethod === 'card' && status === 'pending_payment' && paymentStatus === 'pending') {
    return 'El pago con tarjeta no se completó. Puedes intentar pagar nuevamente.';
  } else if (paymentStatus === 'failed') {
    return 'El pago anterior falló. Puedes intentar pagar nuevamente.';
  } else if (status === 'pending_payment') {
    return 'Tu pedido está confirmado y listo para pago.';
  } else if (status === 'pending') {
    return 'Tu pedido está pendiente de confirmación y pago.';
  }
  return '';
}

/**
 * Obtiene el estilo CSS apropiado para el botón de pago según el estado
 * @param paymentStatus Estado del pago
 * @returns string con las clases CSS
 */
export function getPaymentButtonStyle(paymentStatus: string): string {
  return paymentStatus === 'failed'
    ? 'bg-red-600 text-white hover:bg-red-700'
    : 'bg-blue-600 text-white hover:bg-blue-700';
}

/**
 * Obtiene el texto apropiado para el botón de pago según el estado
 * @param paymentStatus Estado del pago
 * @returns string con el texto del botón
 */
export function getPaymentButtonText(paymentStatus: string): string {
  return paymentStatus === 'failed' ? 'Reintentar pago' : 'Pagar ahora';
} 